import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { Mail, ShieldCheck, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.form.success);
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || c.form.error;
      toast.error(typeof msg === "string" ? msg : c.form.error);
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Mail, label: "Email", value: c.email, href: `mailto:${c.email}` },
    { icon: ShieldCheck, label: "PEC", value: c.pec, href: `mailto:${c.pec}` },
  ];

  const inputBase =
    "w-full bg-transparent border border-white/15 text-white placeholder:text-white/35 px-4 py-3.5 text-sm focus:outline-none focus:border-[#B87333] transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-[#0B1F33] text-white overflow-hidden relative">
      <div className="absolute inset-0 blueprint-grid-dark opacity-30" />
      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: intro + info */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#B87333]" />
                <span className="overline text-[#B87333]">{c.overline}</span>
              </div>
              <h2 className="font-display font-extrabold tracking-tighter leading-[1.02] text-4xl lg:text-5xl">
                {c.title}
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed max-w-md">{c.text}</p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 space-y-1">
                <h4 className="overline text-white/40 mb-4">{c.infoTitle}</h4>
                {info.map((it) => {
                  const Row = (
                    <div className="flex items-center gap-4 py-3 border-b border-white/10 group">
                      <span className="w-10 h-10 grid place-items-center border border-white/15 text-[#B87333] group-hover:bg-[#B87333] group-hover:text-white transition-colors duration-300">
                        <it.icon size={17} strokeWidth={1.6} />
                      </span>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-white/40">{it.label}</div>
                        <div className="text-sm text-white/90 font-medium">{it.value}</div>
                      </div>
                    </div>
                  );
                  return it.href ? (
                    <a key={it.label} href={it.href} data-testid={`contact-info-${it.label.toLowerCase()}`} className="block hover:opacity-90">
                      {Row}
                    </a>
                  ) : (
                    <div key={it.label} data-testid={`contact-info-${it.label.toLowerCase()}`}>{Row}</div>
                  );
                })}
                <div className="pt-4 text-xs text-white/40 tracking-wide">{c.vat}</div>
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form data-testid="contact-form" onSubmit={onSubmit} className="bg-white/[0.03] border border-white/10 p-8 lg:p-10">
                {/* Honeypot — hidden from real users, catches bots */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={onChange}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{c.form.name}</label>
                    <input
                      data-testid="contact-name"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      required
                      minLength={2}
                      className={inputBase}
                      placeholder={c.form.name}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{c.form.email}</label>
                    <input
                      data-testid="contact-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      required
                      className={inputBase}
                      placeholder="nome@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{c.form.phone}</label>
                    <input
                      data-testid="contact-phone"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      className={inputBase}
                      placeholder="+39 ..."
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{c.form.message}</label>
                    <textarea
                      data-testid="contact-message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      minLength={5}
                      rows={5}
                      className={`${inputBase} resize-none`}
                      placeholder={c.form.message}
                    />
                  </div>
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="group mt-8 inline-flex items-center justify-center gap-2 bg-[#B87333] text-white text-sm font-semibold px-8 py-4 hover:bg-white hover:text-[#0B1F33] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  {loading ? c.form.sending : c.form.submit}
                  {!loading && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
